import {
  bringAnnotationForward,
  duplicateAnnotation,
  getDisplayOrderedAnnotations,
  nextAnnotationId,
  normalizeAnnotation,
  normalizeFeedbackPackage,
  sendAnnotationBackward,
  screenIdForStage,
} from './feedbackAnnotations.js';
import {
  DEFAULT_FEEDBACK_COLOR,
  DEFAULT_TEXT_BACKGROUND_MODE,
  normalizeTextBackgroundMode,
} from './feedbackTypes.js';

const TOOL_COLOR_IDS = Object.freeze(['rectangle', 'arrow', 'highlight', 'pin', 'text', 'pen']);

function createActiveColorByTool() {
  return TOOL_COLOR_IDS.reduce((map, toolId) => {
    map[toolId] = DEFAULT_FEEDBACK_COLOR;
    return map;
  }, {});
}

export function createInitialFeedbackState() {
  return {
    sessionActorUserId: null,
    overlayOpen: false,
    captureStatus: 'idle',
    captureMessage: '',
    package: null,
    stage: null,
    lastSourceMetadata: null,
    activeTool: null,
    activeColor: DEFAULT_FEEDBACK_COLOR,
    activeColorByTool: createActiveColorByTool(),
    activeTextBackgroundMode: DEFAULT_TEXT_BACKGROUND_MODE,
    selectedAnnotationId: null,
    textEditingAnnotationId: null,
    workspaceMode: 'edit',
    saveStatus: 'idle',
    saveMessage: '',
    recoveryStatus: 'idle',
    recoveryMessage: '',
    endSessionOpen: false,
    endSessionStatus: 'idle',
    endSessionError: '',
    endSessionDegradedExitAvailable: false,
    storageWarning: '',
    historyItems: [],
    historyStatus: 'idle',
    historyError: '',
    historyAppendError: '',
    historyMeta: {
      total: 0,
      limit: 0,
      offset: null,
      nextCursor: null,
      hasMore: false,
      lifecycleCounts: {
        draft: 0,
        submitted: 0,
        resolved: 0,
      },
    },
    historyOpen: false,
    reviewOpen: false,
    reviewReturnContext: null,
    exportStatus: 'idle',
    exportMessage: '',
    regionSelecting: false,
    regionBox: null,
    elementSelecting: false,
    undoStack: [],
    redoStack: [],
  };
}

function clone(value) {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

function packageWithCounts(pkg, screens) {
  const annotationCount = screens.reduce(
    (sum, screen) => sum + (screen.metadata?.annotations?.length || 0),
    0,
  );
  const routeList = [...new Set(screens.map((screen) => screen.metadata?.route).filter(Boolean))];
  return {
    ...pkg,
    screens,
    screenCount: screens.length,
    annotationCount,
    primaryRoute: routeList[0] || pkg.primaryRoute || null,
    routeList,
  };
}

function normalizePersistedScreen(screen) {
  const {
    saveError: _saveError,
    error: _error,
    saveMessage: _saveMessage,
    lastEditedAt: _lastEditedAt,
    lastAutosaveAt: _lastAutosaveAt,
    ...rest
  } = clone(screen) || {};
  const screenId = screenIdForStage(rest);
  return {
    ...ensureStageDraftFields({
      ...rest,
      screenId,
      saveStatus: 'saved',
      localRevision: 0,
      lastServerSaveAt: rest.lastServerSaveAt || rest.savedAt || null,
    }),
    saveStatus: 'saved',
    localRevision: 0,
    lastEditedAt: null,
    lastAutosaveAt: null,
    lastServerSaveAt: rest.lastServerSaveAt || rest.savedAt || null,
    // Server-known screens carry their capture id explicitly so a later
    // failed save can retry via PATCH instead of re-POSTing.
    persistedCaptureId: rest.persistedCaptureId || screenId,
    // Base marker for the three-way merge: this is the content the server is
    // known to hold right now.
    syncedFingerprint: screenContentFingerprint(rest),
  };
}

function persistedCaptureIdForScreen(pkg, screen) {
  if (pkg?.localOnly) return null;
  if (screen?.saveStatus === 'save_failed') {
    // A failed save only re-POSTs when the capture was never persisted; a
    // known capture id must PATCH (screen-id fallbacks would masquerade
    // never-persisted local screens as persisted ones).
    return screen?.persistedCaptureId || null;
  }
  return screen?.persistedCaptureId || screen?.screenId || screen?.metadata?.screenId || null;
}

function shouldKeepLocalOnlyScreen(pkg, screen) {
  return (
    screen?.saveStatus === 'save_failed' ||
    isDirtyLocalScreen(screen) ||
    !persistedCaptureIdForScreen(pkg, screen)
  );
}

function dedupeBaseScreens(screens, { preferHighestLocalRevision = false } = {}) {
  const orderedIds = [];
  const byId = new Map();
  for (const screen of screens) {
    const screenId = screenIdForStage(screen);
    if (!screenId) continue;
    if (!byId.has(screenId)) {
      orderedIds.push(screenId);
      byId.set(screenId, screen);
      continue;
    }
    if (!preferHighestLocalRevision) continue;
    const current = byId.get(screenId);
    const currentRevision = Number(current?.localRevision) || 0;
    const nextRevision = Number(screen?.localRevision) || 0;
    if (nextRevision > currentRevision) {
      byId.set(screenId, screen);
    }
  }
  return orderedIds.map((screenId) => byId.get(screenId)).filter(Boolean);
}

function isDirtyLocalScreen(screen) {
  return (
    (Number(screen?.localRevision) || 0) > 0 &&
    ['editing', 'autosaving', 'save_failed', 'save_conflict'].includes(screen?.saveStatus)
  );
}

// djb2 over a string — cheap and deterministic.
function djb2(text) {
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) >>> 0;
  }
  return hash.toString(36);
}

// Deterministic content fingerprint for a screen. Annotations are normalized
// (server round-trips add default fields) and hashed in full — they are small.
// The multi-megabyte screenshot data URL is fingerprinted by length plus
// head/tail samples instead of a full serialization pass, so merges never
// build multi-MB strings on the main thread; two different real screenshots
// with identical length AND identical 512-char head/tail are not a realistic
// collision for encoded image data.
export function screenContentFingerprint(screen) {
  const image = screen?.metadata?.image || { width: 1, height: 1 };
  const annotations = (screen?.metadata?.annotations || []).map((annotation, index) =>
    normalizeAnnotation(annotation, index, image),
  );
  const shot = String(screen?.screenshotDataUrl || '');
  const shotSample = `${shot.length}:${djb2(shot.slice(0, 256) + shot.slice(-256))}`;
  return `${djb2(JSON.stringify(annotations))}:${shotSample}`;
}

// A foreign write is a server copy whose content matches NEITHER the local
// content (own echo) NOR the recorded base the server was last known to hold
// (`syncedFingerprint`). No timestamps are consulted — edit clocks race across
// tabs. An unknown base with differing content is treated as foreign:
// surfacing a conflict is safe; silently overwriting either side is not.
export function isForeignServerScreen(serverScreen, localScreen) {
  if (!serverScreen || !localScreen) return false;
  const serverFingerprint = screenContentFingerprint(serverScreen);
  if (serverFingerprint === screenContentFingerprint(localScreen)) return false;
  return serverFingerprint !== (localScreen.syncedFingerprint || null);
}

// Three-way merge (base/server/local) for one screen the server also knows:
// - local clean → the server copy wins (normalized to saved)
// - server content equals local content → own save echo: confirmed saved
// - server content equals the base (syncedFingerprint) → server unchanged
//   since our last sync: keep the local pending edits
// - otherwise a foreign write landed → keep local content, flag save_conflict
function mergeServerScreenWithLocal(serverScreen, localScreen) {
  if (!localScreen || !isDirtyLocalScreen(localScreen)) {
    return normalizePersistedScreen(serverScreen);
  }
  const serverFingerprint = screenContentFingerprint(serverScreen);
  if (serverFingerprint === screenContentFingerprint(localScreen)) {
    return normalizePersistedScreen(serverScreen);
  }
  if (serverFingerprint === (localScreen.syncedFingerprint || null)) {
    return clone(localScreen);
  }
  const kept = clone(localScreen);
  kept.saveStatus = 'save_conflict';
  return kept;
}

export function mergePackageBaseWithLocalScreens(
  basePackage,
  localPackage,
  {
    normalizeBaseScreens = false,
    tombstoneScreenIds = [],
    preserveLocalPackageFields = false,
  } = {},
) {
  if (!basePackage) return localPackage;
  const tombstones = new Set(tombstoneScreenIds);
  const localById = new Map(
    (localPackage?.screens || [])
      .map((screen) => [screenIdForStage(screen), screen])
      .filter(([screenId]) => Boolean(screenId)),
  );
  const preparedBaseScreens = (basePackage.screens || [])
    .filter((screen) => !tombstones.has(screenIdForStage(screen)))
    .map((screen) =>
      normalizeBaseScreens
        ? mergeServerScreenWithLocal(screen, localById.get(screenIdForStage(screen)))
        : clone(screen),
    );
  const baseScreens = dedupeBaseScreens(preparedBaseScreens, {
    preferHighestLocalRevision: !normalizeBaseScreens,
  });
  const baseScreenIds = new Set(
    baseScreens.map((screen) => screenIdForStage(screen)).filter(Boolean),
  );
  const localSurvivorsById = new Map();
  const localOrder = [];

  for (const screen of localPackage?.screens || []) {
    const screenId = screenIdForStage(screen);
    if (
      !screenId ||
      baseScreenIds.has(screenId) ||
      !shouldKeepLocalOnlyScreen(localPackage, screen)
    ) {
      continue;
    }
    if (!localSurvivorsById.has(screenId)) {
      localOrder.push(screenId);
      localSurvivorsById.set(screenId, clone(screen));
      continue;
    }
    const current = localSurvivorsById.get(screenId);
    const currentRevision = Number(current?.localRevision) || 0;
    const nextRevision = Number(screen?.localRevision) || 0;
    if (nextRevision > currentRevision) {
      localSurvivorsById.set(screenId, clone(screen));
    }
  }

  const localScreens = localOrder
    .map((screenId) => localSurvivorsById.get(screenId))
    .filter(Boolean);
  // Screen ids are monotonic (`nextScreenId`), so a stable sort by numeric
  // suffix restores true capture order — otherwise re-appended local-only
  // survivors would jump to the tail behind screens captured after them.
  // Ids without a numeric suffix keep their relative order at the tail.
  const mergedScreens = [...baseScreens, ...localScreens].sort(
    (a, b) => screenIdCaptureOrder(a) - screenIdCaptureOrder(b),
  );
  const mergedPackage = { ...clone(basePackage) };
  // Capture autosaves and deletes return the whole server package, but those
  // responses can predate unsaved edits made in the review form. Keep the two
  // locally editable package fields until the explicit Save/Submit boundary
  // sends them to the server. Property-presence checks preserve intentional
  // empty values as well as non-empty edits.
  if (
    preserveLocalPackageFields &&
    localPackage &&
    Object.prototype.hasOwnProperty.call(localPackage, 'title')
  ) {
    mergedPackage.title = localPackage.title;
  }
  if (
    preserveLocalPackageFields &&
    localPackage &&
    Object.prototype.hasOwnProperty.call(localPackage, 'overallInstructions')
  ) {
    mergedPackage.overallInstructions = localPackage.overallInstructions;
  }
  return packageWithCounts(mergedPackage, mergedScreens);
}

function screenIdCaptureOrder(screen) {
  const match = /^S-(\d+)$/.exec(screenIdForStage(screen) || '');
  return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
}

function ensureStageDraftFields(stage, { markDirty = false } = {}) {
  if (!stage) return stage;
  const currentRevision = Number(stage.localRevision) || 0;
  const next = {
    ...stage,
    screenId: screenIdForStage(stage),
    saveStatus: stage.saveStatus || (stage.persistedCaptureId ? 'saved' : 'idle'),
    localRevision: currentRevision,
    lastEditedAt: stage.lastEditedAt || null,
    lastAutosaveAt: stage.lastAutosaveAt || null,
    lastServerSaveAt: stage.lastServerSaveAt || stage.savedAt || null,
  };
  if (!markDirty) return next;
  return {
    ...next,
    saveStatus: 'editing',
    localRevision: currentRevision + 1,
    lastEditedAt: new Date().toISOString(),
  };
}

function shouldMirrorStageIntoPackage(pkg, stage) {
  if (!pkg || !stage) return false;
  const screenId = screenIdForStage(stage);
  if (!screenId) return false;
  if ((Number(stage.localRevision) || 0) > 0) return true;
  if (stage.persistedCaptureId) return true;
  return (pkg.screens || []).some((screen) => screenIdForStage(screen) === screenId);
}

function syncStageIntoPackage(pkg, stage) {
  if (!shouldMirrorStageIntoPackage(pkg, stage)) return pkg;
  const screenId = screenIdForStage(stage);
  let found = false;
  const screens = (pkg.screens || []).map((screen) => {
    if (screenIdForStage(screen) !== screenId) return screen;
    found = true;
    return clone(ensureStageDraftFields(stage));
  });
  if (!found) screens.push(clone(ensureStageDraftFields(stage)));
  return packageWithCounts(pkg, screens);
}

function removeStageFromPackage(pkg, screenId) {
  if (!pkg || !screenId) return pkg;
  return packageWithCounts(
    pkg,
    (pkg.screens || []).filter((screen) => screenIdForStage(screen) !== screenId),
  );
}

function withStageHistory(state, stage) {
  const nextStage = ensureStageDraftFields(stage, { markDirty: true });
  if (!state.stage) {
    return {
      ...state,
      stage: nextStage,
      package: syncStageIntoPackage(state.package, nextStage),
      redoStack: [],
    };
  }
  return {
    ...state,
    stage: nextStage,
    package: syncStageIntoPackage(state.package, nextStage),
    undoStack: [...state.undoStack, clone(state.stage.metadata.annotations || [])],
    redoStack: [],
  };
}

function updateAnnotationList(stage, mapper) {
  const annotations = stage.metadata.annotations || [];
  return {
    ...stage,
    metadata: {
      ...stage.metadata,
      annotations: annotations.map(mapper),
    },
  };
}

function moveAnnotation(annotation, dx, dy) {
  const next = { ...annotation };
  if (next.bounds) {
    next.bounds = {
      ...next.bounds,
      x: next.bounds.x + dx,
      y: next.bounds.y + dy,
    };
  }
  if (Array.isArray(next.points)) {
    next.points = next.points.map(([x, y]) => [x + dx, y + dy]);
  }
  return next;
}

function nextSelectionAfterDelete(annotations, deletedId, selectedAnnotationId) {
  if (selectedAnnotationId !== deletedId) {
    return annotations.some((annotation) => annotation.id === selectedAnnotationId)
      ? selectedAnnotationId
      : null;
  }
  const orderedBeforeDelete = getDisplayOrderedAnnotations(annotations);
  const deletedIndex = orderedBeforeDelete.findIndex((annotation) => annotation.id === deletedId);
  const orderedAfterDelete = orderedBeforeDelete.filter(
    (annotation) => annotation.id !== deletedId,
  );
  if (orderedAfterDelete.length === 0) return null;
  return orderedAfterDelete[Math.min(deletedIndex, orderedAfterDelete.length - 1)]?.id || null;
}

export function feedbackReducer(state, action) {
  switch (action.type) {
    case 'session/reset':
      return { ...createInitialFeedbackState(), sessionActorUserId: action.actorUserId || null };
    case 'session/clear-active':
      return {
        ...state,
        overlayOpen: false,
        captureStatus: 'idle',
        captureMessage: '',
        package: null,
        stage: null,
        lastSourceMetadata: null,
        activeTool: null,
        selectedAnnotationId: null,
        textEditingAnnotationId: null,
        workspaceMode: 'edit',
        saveStatus: 'idle',
        saveMessage: '',
        recoveryStatus: 'idle',
        recoveryMessage: '',
        endSessionOpen: false,
        endSessionStatus: 'idle',
        endSessionError: '',
        endSessionDegradedExitAvailable: false,
        historyOpen: false,
        reviewOpen: false,
        reviewReturnContext: null,
        exportStatus: 'idle',
        exportMessage: '',
        regionSelecting: false,
        regionBox: null,
        elementSelecting: false,
        undoStack: [],
        redoStack: [],
      };
    case 'overlay/open':
      return { ...state, overlayOpen: true };
    case 'overlay/close':
      return {
        ...state,
        overlayOpen: false,
        historyOpen: false,
        reviewOpen: false,
      };
    case 'capture/status':
      return {
        ...state,
        captureStatus: action.status,
        captureMessage: action.message || '',
      };
    case 'recovery/status':
      return {
        ...state,
        recoveryStatus: action.status,
        recoveryMessage: action.message || '',
      };
    case 'end-session/open':
      return {
        ...state,
        endSessionOpen: true,
        endSessionStatus: 'idle',
        endSessionError: '',
        endSessionDegradedExitAvailable: false,
      };
    case 'end-session/close':
      return {
        ...state,
        endSessionOpen: false,
        endSessionStatus: 'idle',
        endSessionError: '',
        endSessionDegradedExitAvailable: false,
      };
    case 'end-session/status':
      return {
        ...state,
        endSessionStatus: action.status,
        endSessionError: action.error || '',
        endSessionDegradedExitAvailable: action.degradedExitAvailable === true,
      };
    case 'package/set':
      return { ...state, package: action.package };
    case 'package/merge-server': {
      const serverBase = normalizeFeedbackPackage(action.package);
      return {
        ...state,
        package: mergePackageBaseWithLocalScreens(serverBase, state.package, {
          normalizeBaseScreens: true,
          tombstoneScreenIds: action.tombstoneScreenIds || [],
          preserveLocalPackageFields: Boolean(action.preserveLocalPackageFields),
        }),
      };
    }
    case 'package/update':
      return { ...state, package: { ...state.package, ...action.patch } };
    case 'stage/set':
      return {
        ...state,
        stage: ensureStageDraftFields(action.stage),
        lastSourceMetadata: action.stage?.metadata || state.lastSourceMetadata,
        selectedAnnotationId: null,
        textEditingAnnotationId: null,
        elementSelecting: false,
        undoStack: [],
        redoStack: [],
      };
    case 'stage/discard':
      return {
        ...state,
        stage: null,
        selectedAnnotationId: null,
        textEditingAnnotationId: null,
        undoStack: [],
        redoStack: [],
      };
    case 'stage/status': {
      const nextStage = ensureStageDraftFields({
        ...state.stage,
        ...action.patch,
      });
      return {
        ...state,
        stage: nextStage,
        package: syncStageIntoPackage(state.package, nextStage),
      };
    }
    case 'tool/set':
      return {
        ...state,
        activeTool: action.tool ?? null,
        activeColor:
          action.tool && state.activeColorByTool[action.tool]
            ? state.activeColorByTool[action.tool]
            : state.activeColor,
      };
    case 'color/set':
      return {
        ...state,
        activeColor: action.color,
        activeColorByTool:
          state.activeTool && state.activeColorByTool[state.activeTool] !== undefined
            ? {
                ...state.activeColorByTool,
                [state.activeTool]: action.color,
              }
            : state.activeColorByTool,
      };
    case 'text-background/set':
      return { ...state, activeTextBackgroundMode: normalizeTextBackgroundMode(action.mode) };
    case 'annotations/replace': {
      const stage = {
        ...state.stage,
        metadata: {
          ...state.stage.metadata,
          annotations: action.annotations,
        },
      };
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId:
          action.selectedAnnotationId !== undefined
            ? action.selectedAnnotationId
            : state.selectedAnnotationId,
        textEditingAnnotationId:
          state.textEditingAnnotationId &&
          action.annotations.some((annotation) => annotation.id === state.textEditingAnnotationId)
            ? state.textEditingAnnotationId
            : null,
      };
    }
    case 'annotation/add': {
      const stage = {
        ...state.stage,
        metadata: {
          ...state.stage.metadata,
          annotations: [...(state.stage.metadata.annotations || []), action.annotation],
        },
      };
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId: action.annotation.id,
        textEditingAnnotationId: action.startTextEditing ? action.annotation.id : null,
        activeTool: null,
      };
    }
    case 'annotation/select':
      return {
        ...state,
        selectedAnnotationId: action.id,
        textEditingAnnotationId: null,
        activeTool: null,
      };
    case 'annotation/clear-selection':
      return {
        ...state,
        selectedAnnotationId: null,
        textEditingAnnotationId: null,
      };
    case 'annotation/update': {
      const stage = updateAnnotationList(state.stage, (annotation) =>
        annotation.id === action.id ? { ...annotation, ...action.patch } : annotation,
      );
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId: action.id,
      };
    }
    case 'annotation/move': {
      const stage = updateAnnotationList(state.stage, (annotation) =>
        annotation.id === action.id ? moveAnnotation(annotation, action.dx, action.dy) : annotation,
      );
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId: action.id,
      };
    }
    case 'annotation/duplicate': {
      const annotations = duplicateAnnotation(state.stage?.metadata?.annotations || [], action.id);
      const duplicatedId = nextAnnotationId(state.stage?.metadata?.annotations || []);
      const stage = {
        ...state.stage,
        metadata: {
          ...state.stage.metadata,
          annotations,
        },
      };
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId: duplicatedId,
        textEditingAnnotationId: null,
      };
    }
    case 'annotation/bring-forward': {
      const stage = {
        ...state.stage,
        metadata: {
          ...state.stage.metadata,
          annotations: bringAnnotationForward(state.stage?.metadata?.annotations || [], action.id),
        },
      };
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId: action.id,
      };
    }
    case 'annotation/send-backward': {
      const stage = {
        ...state.stage,
        metadata: {
          ...state.stage.metadata,
          annotations: sendAnnotationBackward(state.stage?.metadata?.annotations || [], action.id),
        },
      };
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId: action.id,
      };
    }
    case 'annotation/delete': {
      const annotations = state.stage.metadata.annotations || [];
      const stage = {
        ...state.stage,
        metadata: {
          ...state.stage.metadata,
          annotations: annotations.filter((annotation) => annotation.id !== action.id),
        },
      };
      return {
        ...withStageHistory(state, stage),
        selectedAnnotationId: nextSelectionAfterDelete(
          annotations,
          action.id,
          state.selectedAnnotationId,
        ),
        textEditingAnnotationId: null,
      };
    }
    case 'annotation/edit/start':
      return {
        ...state,
        selectedAnnotationId: action.id,
        textEditingAnnotationId: action.id,
        activeTool: null,
      };
    case 'annotation/edit/stop':
      return {
        ...state,
        textEditingAnnotationId: null,
      };
    case 'history/undo': {
      if (!state.stage || state.undoStack.length === 0) return state;
      const previous = state.undoStack[state.undoStack.length - 1];
      const current = clone(state.stage.metadata.annotations || []);
      const nextStage = ensureStageDraftFields(
        {
          ...state.stage,
          metadata: { ...state.stage.metadata, annotations: previous },
        },
        { markDirty: true },
      );
      return {
        ...state,
        stage: nextStage,
        package: syncStageIntoPackage(state.package, nextStage),
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, current],
        textEditingAnnotationId: null,
      };
    }
    case 'history/redo': {
      if (!state.stage || state.redoStack.length === 0) return state;
      const next = state.redoStack[state.redoStack.length - 1];
      const current = clone(state.stage.metadata.annotations || []);
      const nextStage = ensureStageDraftFields(
        {
          ...state.stage,
          metadata: { ...state.stage.metadata, annotations: next },
        },
        { markDirty: true },
      );
      return {
        ...state,
        stage: nextStage,
        package: syncStageIntoPackage(state.package, nextStage),
        undoStack: [...state.undoStack, current],
        redoStack: state.redoStack.slice(0, -1),
        textEditingAnnotationId: null,
      };
    }
    case 'save/status':
      return {
        ...state,
        saveStatus: action.status,
        saveMessage: action.message || '',
      };
    case 'storage/warn':
      return { ...state, storageWarning: action.message || '' };
    case 'history/status':
      return {
        ...state,
        historyStatus: action.status,
        historyError: action.error || '',
        historyAppendError: action.appendError || '',
        historyItems: action.items || state.historyItems,
        historyMeta: action.meta || state.historyMeta,
      };
    case 'history/open':
      return { ...state, historyOpen: true };
    case 'history/close':
      return { ...state, historyOpen: false };
    case 'review/open':
      return { ...state, reviewOpen: true };
    case 'review/close':
      return { ...state, reviewOpen: false };
    case 'review/return-context':
      return { ...state, reviewReturnContext: action.context || null };
    case 'workspace/mode':
      return { ...state, workspaceMode: action.mode || 'edit' };
    case 'export/status':
      return {
        ...state,
        exportStatus: action.status,
        exportMessage: action.message || '',
      };
    case 'region/start':
      return { ...state, regionSelecting: true, regionBox: null };
    case 'region/update':
      return { ...state, regionBox: action.region };
    case 'region/cancel':
      return { ...state, regionSelecting: false, regionBox: null };
    case 'element/start':
      return { ...state, elementSelecting: true };
    case 'element/cancel':
      return { ...state, elementSelecting: false };
    case 'package/screen/remove':
      return {
        ...state,
        package: removeStageFromPackage(state.package, action.screenId),
      };
    // Update save-state fields on a package screen when no live stage mirrors
    // it any more (e.g. the save that runs while a stage is closing fails).
    // Never touches localRevision, so the autosave scheduler cannot loop.
    case 'package/screen/status': {
      if (!state.package || !action.screenId) return state;
      const screens = (state.package.screens || []).map((screen) =>
        screenIdForStage(screen) === action.screenId ? { ...screen, ...action.patch } : screen,
      );
      return { ...state, package: packageWithCounts(state.package, screens) };
    }
    default:
      return state;
  }
}
